import React, { useState, useReducer, useMemo, useEffect } from 'react'
import { Row, Col } from 'react-flexbox-grid'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import AbcData from './fragments/AbcData'
import Button from 'components/Button'
import { ContentBlock, ExpandableBlocks } from 'components/ContentBlock'
import { Select, Checkbox } from 'components/forms'
import { CashRegisters, TimePeriodFilter, filterReducer } from '../filterFragments'
import { initAbcStatistics, getStatisticsData } from '../../gql/getAbcStatistics'
import { listProducts } from 'sections/product/gql/query'
import Divider from 'components/Divider'
import { useStatisticsData } from '../helpers'
import { CardsRow, CardCol } from '../filterFragments/styled'
import FilterCardRemote from 'components/FilterCard/FilterCardRemote'
import isEmpty from 'lodash/isEmpty'
import { listCashRegisters } from 'gql/query'
import { useQuery } from 'components/graphql'

const AbcStatisticsFilter = () => {
	const [expandedId, setExpandedId] = useState(null)
	const [compareDatePeriod, setCompareDatePeriod] = useState(null)
	const [visibleCompare, setVisibleCompare] = useState(false)
	const [finishData, setFinishData] = useState(null)
	const [t] = useTranslation()
	const { data: crData } = useQuery(listCashRegisters, { fetchPolicy: 'cache-first' })

	const [filterPrepare, dispatchFilter] = useReducer(filterReducer, {
		from: moment().utc().startOf('day').toISOString(),
		to: moment().utc().endOf('day').toISOString(),
		cashRegisterIds: crData?.listCashRegisters?.map(({ id }) => id) || [],
		productIds: [],
		groupBy: 'product',
		unitedAgainstWaste: false,
		goodsFlow: true
	})

	const { getStatistics, status, data, loading, called } = useStatisticsData({
		getQuery: getStatisticsData,
		initQuery: initAbcStatistics
	})
	const { getStatistics: getStatisticsCompare, data: compareData, loading: compareLoading } = useStatisticsData({
		getQuery: getStatisticsData,
		initQuery: initAbcStatistics
	})
	useEffect(() => {
		if(compareDatePeriod) {
			setVisibleCompare(true)
			return
		}
		setVisibleCompare(false)
	}, [compareDatePeriod])
	useEffect(() => {
		let updateData = [...data]
		if(!isEmpty(data) && !isEmpty(compareData)) {
			compareData.forEach(item => {
				const nameKeys = Object.keys(item)
				const obj = nameKeys.reduce((acc, el) => {
					acc[`${el}_compare`] = item[el]
					return acc
				}, {})
				const index = data.findIndex(it => it.productId === obj.productId_compare)
				if(index !== -1) {
					updateData[index] = { ...data[index], ...obj }
				}
			})
			setFinishData(updateData)
		}
	}, [data, compareData])
	const { from, to, cashRegisterIds, productIds, groupBy, unitedAgainstWaste, goodsFlow } = filterPrepare
	const handleSetFilter = () => {
		getStatistics({ variables: { params: filterPrepare } })
		if(!isEmpty(compareDatePeriod)) {
			const compareParams = { ...filterPrepare, from: compareDatePeriod.from, to: compareDatePeriod.to }
			getStatisticsCompare({ variables: { params: compareParams } })
		}
		setExpandedId('1')
	}

	const groupByOptions = {
		product: {
			groupByColumns: [],
			filterColumns: ['shopName', 'dateTime']
		},
		shopProduct: {
			groupByColumns: ['shopName'],
			filterColumns: ['dateTime']
		},
		productShop: {
			groupByColumns: ['name'],
			filterColumns: ['dateTime']
		},
		dateProduct: {
			groupByColumns: ['dateTime'],
			filterColumns: ['shopName']
		},
		productDate: {
			groupByColumns: ['name'],
			filterColumns: ['shopName']
		},
		productDateShop: {
			groupByColumns: ['name', 'dateTime'],
			filterColumns: []
		}
	}
	const { groupByColumns, filterColumns } = groupByOptions[filterPrepare.groupBy]
	const productText = useMemo(() => t('common.strings.product'), [t])
	const shopText = useMemo(() => t('common.strings.branch'), [t])
	const dateText = useMemo(() => t('common.fields.date'), [t])

	return(
		<ExpandableBlocks expandedId={expandedId} onExpand={setExpandedId}>
			<ContentBlock blockId='0' startExpanded={!called} label={t('common.strings.settings')}>
				<Row>
					<Col xs>
						<Select
							noForm
							value={groupBy}
							label={t('statistic_section.strings.grouped_by')}
							onChange={({ value }) => dispatchFilter({ type: 'groupBy', value })}
							options={[
								{ label: productText, value: 'product' },
								{ label: `${shopText}-${productText}`, value: 'shopProduct' },
								{ label: `${productText}-${shopText}`, value: 'productShop' },
								{ label: `${dateText}-${productText}`, value: 'dateProduct' },
								{ label: `${productText}-${dateText}`, value: 'productDate' },
								{ label: `${productText}-${dateText}-${shopText}`, value: 'productDateShop' }
							]}
						/>
						<Checkbox
							noForm
							onChange={e => dispatchFilter({ type: 'goodsFlow', value: e.target.checked })}
							checked={goodsFlow}
							label={t('statistic_section.fields.goods_flow')}
						/>
						<Checkbox
							noForm
							onChange={e => dispatchFilter({ type: 'unitedAgainstWaste', value: e.target.checked })}
							checked={unitedAgainstWaste}
							label={t('statistic_section.fields.united_against_waste')}
						/>
						<Checkbox
							disabled={!compareDatePeriod}
							noForm
							onChange={e => setVisibleCompare(e.target.checked)}
							checked={visibleCompare}
							label={t('statistic_section.strings.show_comparison_columns')}
						/>
					</Col>
				</Row>
				<Divider />
				<div>
					<ExpandableBlocks nested>
						<ContentBlock startExpanded label={t('common.buttons.filter')}>
							<CardsRow>
								<CardCol>
									<TimePeriodFilter
										value={{ to, from }}
										onChange={value => dispatchFilter({ value, type: 'datePeriod' })}
									/>
								</CardCol>
								<CardCol>
									<CashRegisters
										value={cashRegisterIds}
										onChange={value => dispatchFilter({ value, type: 'cashRegisterIds' })}
									/>
								</CardCol>
								<CardCol>
									<FilterCardRemote
										listQuery={listProducts}
										label={t('common.strings.product')}
										allItemsLabel={t('common.strings.all_products')}
										value={productIds}
										onChange={value => dispatchFilter({ value, type: 'productIds' })}
										withSearch={true}
									/>
								</CardCol>
								<CardCol>
									<TimePeriodFilter
										emptyLabel={t('statistic_section.strings.not_selected')}
										label={t('statistic_section.strings.compare_with')}
										value={{ from: compareDatePeriod?.from, to: compareDatePeriod?.to }}
										onChange={value => setCompareDatePeriod(value)}
										onRemoveItem={() => setCompareDatePeriod(null)}
										startValue
									/>
								</CardCol>
								{/*<CardCol>
									<CardItemList
										items={[]}
										ItemComponent={({ item }) => item}
										height={200}
										label='Warengruppe'
									/>
								</CardCol>*/}
							</CardsRow>
						</ContentBlock>
					</ExpandableBlocks>
				</div>
				<Divider />
				<Col type='row'>
					<Button text={t('common.buttons.calculate')} onClick={handleSetFilter}/>
				</Col>
			</ContentBlock>
			{called &&
				<AbcData
					key={filterPrepare?.groupBy}
					loading={loading && compareLoading}
					tableLoading={loading}
					data={finishData || data}
					compareData={compareData}
					groupByColumns={groupByColumns}
					filterColumns={filterColumns}
					visibleCompare={visibleCompare}
					{...status === 'ERROR' && { noDataText: t('server_errors.request_failed') }}
				/>
			}
		</ExpandableBlocks>
	)
}

export default AbcStatisticsFilter
